/* ──────────────────────────────────────────────────────────────────────────
 * Multi-hazard correlation — §9.
 *
 * The brief puts the innovation precisely where it belongs:
 *
 *   "Heat + high crowd density + low water availability + medical-case
 *    increase should create a different risk picture than any individual
 *    signal alone."
 *
 * That word "different" is the whole design constraint. A dashboard that
 * shows four gauges and colours the worst one has not correlated anything —
 * it has just picked a maximum. The test suite asserts the opposite
 * explicitly: a compound score must be able to exceed every one of its
 * component scores, because the hazards make each other worse.
 *
 * The mechanism is deliberately simple and inspectable: each hazard has a
 * severity in 0..1, pairs that are known to compound carry a named
 * interaction weight, and the compound score is the strongest single hazard
 * plus the interactions that are actually live. There is no learned model
 * here and none is claimed — the coefficients are stated, arguable, and
 * printed in the UI.
 *
 * Why rules rather than a model: a learned correlation needs incident
 * history that does not exist for a Kumbh that has not happened. A stated
 * rule can be challenged by a doctor or a sanitation engineer on the day.
 * A fitted coefficient with no training data can only be believed.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { ASSEMBLY_AREA_M2 } from "./crowdSafety";
import { SERVICE_STANDARDS } from "./serviceStress";

const clamp = (v) => Math.max(0, Math.min(1, v));

/* ── Environment ───────────────────────────────────────────────────────
 * Simhastha 2027 runs through the hot pre-monsoon months. A deterministic
 * diurnal temperature curve for Nashik, so the heat hazard moves with the
 * same clock as everything else and a reviewer can see the afternoon peak
 * line up with the midday darshan crowd. Not a forecast — a plausible day. */
export function environment(minuteOfDay) {
  const m = ((minuteOfDay % 1440) + 1440) % 1440;
  /* Minimum ~24 C around 05:00, maximum ~39 C around 15:00. */
  const phase = ((m - 300) / 1440) * 2 * Math.PI;
  const tempC = 31.5 - 7.5 * Math.cos(phase);
  /* Humidity runs inverse to temperature. */
  const humidity = 62 - 22 * Math.cos(phase + Math.PI);

  /* Heat index matters more than dry-bulb temperature for a crowd standing
   * in the sun: a body sheds heat by evaporation, and humid air will not
   * take it. Simplified Steadman-style approximation. */
  const vapour = (humidity / 100) * 6.105
    * Math.exp((17.27 * tempC) / (237.7 + tempC));
  const heatIndex = tempC + 0.33 * vapour - 4;

  /* Rainfall and river level. Pre-monsoon convective showers build in the
   * late afternoon, and the Godavari responds with a lag of a few hours —
   * which is the point: the river hazard peaks when the rain has stopped
   * and nobody is looking at the sky any more. */
  const rainWindow = Math.exp(-((m - 16.5 * 60) ** 2) / (2 * 110 ** 2));
  const rainfallMmPerHr = Number((rainWindow * 14).toFixed(1));
  const riverLagged = Math.exp(-((m - 19.5 * 60) ** 2) / (2 * 150 ** 2));
  const riverLevelM = Number((2.1 + riverLagged * 1.6).toFixed(2));

  return {
    minuteOfDay: m,
    tempC: Number(tempC.toFixed(1)),
    humidity: Math.round(humidity),
    heatIndex: Number(heatIndex.toFixed(1)),
    rainfallMmPerHr,
    riverLevelM,
    RIVER_WARNING_M,
    RIVER_DANGER_M,
    daylight: m >= 360 && m <= 1110,
    trust: TRUST.SIMULATED,
  };
}

/* Godavari ghat-step levels. Invented for the demo; a real deployment reads
 * the Water Resources Department gauge. */
export const RIVER_WARNING_M = 3.0;
export const RIVER_DANGER_M = 3.6;

/* ── Individual hazards ────────────────────────────────────────────────
 * Each returns severity 0..1 plus the reading that produced it, so a
 * compound alert can always be taken apart again. */

function heatHazard(env) {
  /* Thresholds follow the shape of IMD heat advisories: caution from the
   * high 30s, danger in the mid 40s heat index. */
  return {
    key: "heat",
    monitored: true,
    label: { en: "Heat", mr: "उष्णता" },
    severity: clamp((env.heatIndex - 32) / 16),
    reading: `${env.heatIndex} heat index (${env.tempC} C, ${env.humidity}% RH)`,
  };
}

function densityHazard(sector) {
  const area = ASSEMBLY_AREA_M2[sector.id];
  if (!area) {
    /* No footprint mapped means no density. Returning zero here would let a
     * sector with unknown area sit quietly at the bottom of the ranking,
     * which is exactly the failure mode worth avoiding. */
    return {
      key: "density",
      label: { en: "Crowd density", mr: "गर्दी घनता" },
      severity: null,
      reading: "assembly area not mapped for this sector",
    };
  }
  const density = sector.current / area;
  return {
    key: "density",
    monitored: true,
    label: { en: "Crowd density", mr: "गर्दी घनता" },
    severity: clamp((density - 1.5) / 3.5),   // band C begins ~2/m², F ~5/m²
    reading: `${density.toFixed(2)} persons/m2`,
    density,
  };
}

function waterHazard(sector) {
  const std = SERVICE_STANDARDS.water;
  const units = Math.max(1, Math.round((sector.capacity / 1000) * std.unitsPer1000));
  const util = (sector.current * std.usesPerPersonPerHour) / (units * std.usesPerUnitPerHour);
  return {
    key: "water",
    monitored: true,
    label: { en: "Water availability", mr: "पाणी उपलब्धता" },
    severity: clamp((util - 0.5) / 0.7),
    reading: `${Math.round(util * 100)}% of drinking-water capacity in demand`,
    utilisation: util,
  };
}

function medicalHazard(sector, env, densitySeverity) {
  /* Presentation rate is not independent of the other hazards — that is the
   * point of the section. It rises with heat and with density, because the
   * cases a Kumbh medical post actually sees are heat exhaustion, syncope
   * and crush-related injury rather than a constant background rate. */
  const heatMultiplier = 1 + clamp((env.heatIndex - 36) / 10) * 1.5;
  const crowdMultiplier = 1 + (densitySeverity ?? 0) * 1.2;
  const std = SERVICE_STANDARDS.medical;
  const perHour = sector.current * std.usesPerPersonPerHour
    * heatMultiplier * crowdMultiplier;

  const units = Math.max(1, Math.round((sector.capacity / 1000) * std.unitsPer1000));
  const util = perHour / (units * std.usesPerUnitPerHour);

  return {
    key: "medical",
    monitored: true,
    label: { en: "Medical load", mr: "वैद्यकीय भार" },
    severity: clamp((util - 0.5) / 1.5),
    reading: `~${Math.round(perHour)} presentations/hour against ${units} post${units === 1 ? "" : "s"}`,
    casesPerHour: Math.round(perHour),
  };
}

/* ── The hazards with no feed ──────────────────────────────────────────
 * The brief names fourteen hazard types. This POC can derive ten of them
 * from the live model, the twin and the service engine. Four — fire,
 * disease, structural failure and security — have no data source at all.
 *
 * They are still listed, with severity null and monitored false, and the
 * reason is stated. Two alternatives were available and both are worse:
 * omitting them makes the coverage look complete when it is not, and
 * scoring them zero makes an unmonitored hazard rank as safe. A commander
 * who cannot see that fire is unmonitored will assume somebody is watching.
 *
 * §16 reads this directly — an unmonitored hazard is an assurance finding,
 * not a gap to be quietly tidied away. */
const UNMONITORED = {
  fire: {
    label: { en: "Fire", mr: "आग" },
    reason: "No fire-detection feed is integrated. Fire status would come from the Fire Department control room and camp thermal monitoring.",
  },
  disease: {
    label: { en: "Disease / epidemic", mr: "रोग / साथ" },
    reason: "No syndromic surveillance feed is integrated. Signals would come from IDSP and medical-camp presentation coding.",
  },
  structural: {
    label: { en: "Structural / infrastructure", mr: "संरचनात्मक" },
    reason: "No structural monitoring is integrated. Bridge, barricade and temporary-structure condition would come from PWD inspection and load sensors.",
  },
  security: {
    label: { en: "Security incident", mr: "सुरक्षा घटना" },
    reason: "No security feed is integrated. Status would come from the police control room and the incident stream.",
  },
};

function unmonitoredHazards() {
  return Object.entries(UNMONITORED).map(([key, v]) => ({
    key,
    label: v.label,
    severity: null,
    monitored: false,
    reading: v.reason,
  }));
}

/* Which sectors are actually on the Godavari. A sector away from the water
 * is not made safer by a low river — the hazard simply does not apply, and
 * saying so is different from scoring it zero. */
const RIVERSIDE_SECTORS = new Set(["SEC-01", "SEC-02", "SEC-03", "SEC-04"]);

/* ── Derived environmental hazards ─────────────────────────────────────── */

function rainfallHazard(env) {
  /* IMD-style bands: light below 7.5 mm/hr, heavy above 15. A crowd on wet
   * ghat steps is a slip-and-fall problem well before the rain becomes a
   * flood problem. */
  return {
    key: "rainfall",
    label: { en: "Rainfall", mr: "पर्जन्य" },
    severity: clamp((env.rainfallMmPerHr - 2) / 20),
    monitored: true,
    reading: `${env.rainfallMmPerHr} mm/hr`,
  };
}

function floodHazard(env, sector) {
  if (!RIVERSIDE_SECTORS.has(sector.id)) {
    return {
      key: "flood",
      label: { en: "River / flood", mr: "नदी / पूर" },
      severity: null,
      monitored: true,
      notApplicable: true,
      reading: "sector is not on the river",
    };
  }
  return {
    key: "flood",
    label: { en: "River / flood", mr: "नदी / पूर" },
    severity: clamp((env.riverLevelM - RIVER_WARNING_M + 0.6) / 1.2),
    monitored: true,
    reading: `${env.riverLevelM} m (warning ${RIVER_WARNING_M} m, danger ${RIVER_DANGER_M} m)`,
  };
}

/* ── Derived operational hazards ───────────────────────────────────────── */

function trafficHazard(sector, context) {
  const chain = context?.mobility;
  if (!chain) {
    return {
      key: "traffic",
      label: { en: "Traffic congestion", mr: "वाहतूक कोंडी" },
      severity: null, monitored: false,
      reading: "mobility chain not supplied to the correlator",
    };
  }
  /* Read from the vehicle-side stages, because that is where a road
   * problem actually shows up. */
  const vehicular = chain.stages.filter((st) => ["bus", "parking"].includes(st.stage));
  const worst = Math.max(...vehicular.map((st) => st.utilisation ?? 0));
  return {
    key: "traffic",
    label: { en: "Traffic congestion", mr: "वाहतूक कोंडी" },
    severity: clamp((worst - 0.7) / 0.8),
    monitored: true,
    reading: `bus/parking stages at ${Math.round(worst * 100)}% of capacity`,
  };
}

function routeBlockageHazard(sector, context) {
  const twin = context?.twin;
  if (!twin) {
    return {
      key: "routeBlockage",
      label: { en: "Route blockage", mr: "मार्ग अडथळा" },
      severity: null, monitored: false,
      reading: "twin not supplied to the correlator",
    };
  }
  const corridors = twin.byType("corridor").filter((c) => c.sector === sector.id);
  const barricades = twin.byType("barricade").filter((b) => b.sector === sector.id);
  if (!corridors.length) {
    return {
      key: "routeBlockage",
      label: { en: "Route blockage", mr: "मार्ग अडथळा" },
      severity: null, monitored: true,
      reading: "no corridor mapped in this sector",
    };
  }
  /* A barricade in a holding configuration is a deliberate blockage. It is
   * still a hazard to anyone downstream of it. */
  const holding = barricades.filter((b) => b.configuration === "holding").length;
  const restrictive = barricades.filter((b) => b.configuration === "single-file").length;
  return {
    key: "routeBlockage",
    label: { en: "Route blockage", mr: "मार्ग अडथळा" },
    severity: clamp((holding * 0.5 + restrictive * 0.3) / Math.max(1, corridors.length)),
    monitored: true,
    reading: `${holding} holding, ${restrictive} single-file across ${corridors.length} corridor(s)`,
  };
}

function utilityHazard(sector, context) {
  const rows = context?.serviceStress;
  if (!rows) {
    return {
      key: "utility",
      label: { en: "Utility / service failure", mr: "सेवा बिघाड" },
      severity: null, monitored: false,
      reading: "service stress not supplied to the correlator",
    };
  }
  const mine = rows.filter((r) => r.sectorId === sector.id);
  if (!mine.length) {
    return {
      key: "utility",
      label: { en: "Utility / service failure", mr: "सेवा बिघाड" },
      severity: null, monitored: true, reading: "no services assessed here",
    };
  }
  const failed = mine.filter((r) => r.state === "failed").length;
  const stressed = mine.filter((r) => r.state === "stressed").length;
  const worst = Math.max(...mine.map((r) => r.utilisation));
  return {
    key: "utility",
    label: { en: "Utility / service failure", mr: "सेवा बिघाड" },
    severity: clamp((worst - 0.6) / 0.6),
    monitored: true,
    reading: `${failed} failed, ${stressed} stressed of ${mine.length} services`,
  };
}

/* Incident-derived hazards. Present only when the incident stream is
 * supplied; absent is reported as unmonitored, not as quiet. */
function incidentHazard(key, label, matcher, sector, context) {
  const incidents = context?.incidents;
  if (!incidents) {
    return {
      key, label, severity: null, monitored: false,
      reading: "incident stream not supplied to the correlator",
    };
  }
  const mine = incidents.filter((i) => i.sector === sector.id && matcher(i));
  const open = mine.filter((i) => !["resolved", "cancelled"].includes(i.status));
  return {
    key, label,
    severity: clamp(open.length / 3),
    monitored: true,
    reading: `${open.length} open of ${mine.length} reported`,
  };
}

function commsHazard(sector, context) {
  const assurance = context?.assurance;
  const total = assurance?.totalFeeds ?? 0;
  if (!assurance) {
    return {
      key: "comms",
      label: { en: "Communication failure", mr: "संपर्क बिघाड" },
      severity: null, monitored: false,
      reading: "assurance layer not supplied to the correlator",
    };
  }
  return {
    key: "comms",
    label: { en: "Communication failure", mr: "संपर्क बिघाड" },
    severity: total ? clamp((assurance.degradedFeeds ?? 0) / total) : null,
    monitored: total > 0,
    reading: total
      ? `${assurance.degradedFeeds ?? 0} of ${total} feeds degraded`
      : "no feeds registered",
  };
}

function egressHazard(sector) {
  const ratio = sector.ingressEgressRatio;
  if (ratio == null) {
    return {
      key: "egress",
      label: { en: "Egress balance", mr: "निर्गमन संतुलन" },
      severity: null,
      reading: "ingress/egress not computed",
    };
  }
  return {
    key: "egress",
    monitored: true,
    label: { en: "Egress balance", mr: "निर्गमन संतुलन" },
    severity: clamp((ratio - 0.7) / 1.0),
    reading: `${ratio}x — people entering per unit of exit capacity`,
    ratio,
  };
}

/* ── Interactions ──────────────────────────────────────────────────────
 * The named pairs. Each carries the operational reason it compounds,
 * because an unexplained coefficient is the thing a commander is entitled
 * to reject. Weight is the extra severity a live pair contributes on top of
 * the strongest single hazard. */
export const INTERACTIONS = [
  {
    pair: ["density", "egress"], weight: 0.34,
    why: "High density with unfavourable egress is the specific combination that precedes a crush. Neither reading alone is alarming; together they are the classic precursor.",
  },
  {
    pair: ["heat", "density"], weight: 0.30,
    why: "A dense crowd cannot shed heat: bodies radiate into each other and there is nowhere to move to. Heat casualties rise faster than either signal alone predicts.",
  },
  {
    pair: ["heat", "water"], weight: 0.28,
    why: "Heat drives fluid loss exactly when water points are already at capacity. Dehydration is the most common serious presentation at a summer mela.",
  },
  {
    pair: ["density", "medical"], weight: 0.26,
    why: "A casualty inside a dense crowd cannot be reached quickly, and the extraction attempt itself disturbs the crowd. Response time degrades as density rises.",
  },
  {
    pair: ["heat", "medical"], weight: 0.22,
    why: "Heat raises both the number and the severity of presentations, so a medical post at nominal capacity is in fact already saturated.",
  },
  {
    pair: ["water", "medical"], weight: 0.20,
    why: "Water shortage converts into medical load with a lag of roughly an hour, so a water queue now is a medical queue later.",
  },
  {
    pair: ["rainfall", "density"], weight: 0.31,
    why: "Wet ghat steps under a dense crowd is the classic slip-and-fall cascade: one person down in a packed column becomes a pile-up before anyone can stop moving.",
  },
  {
    pair: ["flood", "density"], weight: 0.38,
    why: "A rising river shrinks the usable ghat while the crowd on it stays the same size. Effective density rises without a single extra person arriving — the most under-appreciated crush mechanism at a riverside mela.",
  },
  {
    pair: ["flood", "egress"], weight: 0.29,
    why: "Water covering the lower steps removes exactly the routes people would use to disperse, so the river degrades ingress and egress at the same time.",
  },
  {
    pair: ["rainfall", "comms"], weight: 0.18,
    why: "Heavy rain degrades both PA intelligibility and radio propagation at the moment crowd instructions matter most.",
  },
  {
    pair: ["routeBlockage", "egress"], weight: 0.33,
    why: "A blocked route is only a nuisance until the sector needs to empty. Combined with tight egress it is the difference between a slow disperse and nowhere to go.",
  },
  {
    pair: ["traffic", "utility"], weight: 0.19,
    why: "Congested approach roads delay resupply of water, food and sanitation servicing, so a traffic problem becomes a service failure a few hours later.",
  },
  {
    pair: ["density", "missingPersons"], weight: 0.17,
    why: "Separations rise sharply with density, and each search party moving against the flow further disturbs a crowd that is already packed.",
  },
  {
    pair: ["heat", "utility"], weight: 0.24,
    why: "Heat raises demand on exactly the services — water, shade, medical — that are already the constraint, so the two curves peak together rather than separately.",
  },
];

/* ── The §9 output ─────────────────────────────────────────────────────── */
/* `context` supplies the other engines' output so the correlator can derive
 * the hazards that are not purely environmental. Each one is optional: a
 * missing context source produces an UNMONITORED hazard with the reason
 * stated, never a quiet zero. */
export function correlateHazards(liveState, context = {}) {
  const env = environment(liveState.minuteOfDay);
  const heat = heatHazard(env);
  const rainfall = rainfallHazard(env);
  const unmonitored = unmonitoredHazards();

  const sectors = liveState.sectors.map((sector) => {
    const density = densityHazard(sector);
    const water = waterHazard(sector);
    const medical = medicalHazard(sector, env, density.severity);
    const egress = egressHazard(sector);
    /* The brief's fourteen hazard types, plus water availability and
     * egress balance which §7 and §10 make measurable here. Order is
     * deliberate: derived hazards first, unmonitored ones last, so a panel
     * that truncates the list truncates the least informative end. */
    const hazards = [
      density,                                    // 1. crowd surge
      egress,                                     // (§7 — not in the 14, but measurable)
      heat,                                       // 5. heat
      rainfall,                                   // 4. heavy rainfall
      floodHazard(env, sector),                   // 3. flood / river
      medical,                                    // 10. medical surge
      water,                                      // (feeds 11)
      utilityHazard(sector, context),             // 11. utility / service failure
      trafficHazard(sector, context),             // 8. traffic congestion
      routeBlockageHazard(sector, context),       // 13. route blockage
      commsHazard(sector, context),               // 14. communication failure
      incidentHazard("missingPersons",            // 9. missing persons
        { en: "Missing persons", mr: "हरवलेल्या व्यक्ती" },
        (i) => i.category === "missing_person", sector, context),
      ...unmonitored,                             // 2, 6, 7, 12 — no feed
    ];

    const sev = {};
    hazards.forEach((h) => { sev[h.key] = h.severity; });

    /* Only a hazard that is measurably present participates. A null
     * severity — unknown area, unmeasured ratio — contributes nothing and
     * is reported as unknown rather than silently treated as zero. */
    const live = hazards.filter((h) => h.severity != null && h.severity > 0.15);
    /* Three distinct kinds of "no number", kept apart because they mean
     * different things to a commander: nobody is watching this; it is
     * watched but does not apply here; it is watched and reads nothing. */
    const unmonitoredHere = hazards.filter((h) => h.monitored === false);
    const notApplicable = hazards.filter((h) => h.notApplicable === true);
    const unknown = hazards.filter(
      (h) => h.severity == null && h.monitored !== false && !h.notApplicable,
    );

    const strongest = live.reduce(
      (a, h) => (h.severity > (a?.severity ?? -1) ? h : a), null,
    );

    const active = INTERACTIONS
      .map((ix) => {
        const [a, b] = ix.pair;
        if (sev[a] == null || sev[b] == null) return null;
        /* Both sides must be meaningfully present: the geometric mean is
         * used so one near-zero reading cannot be carried by a high one. */
        const strength = Math.sqrt(sev[a] * sev[b]);
        if (strength < 0.25) return null;
        return {
          ...ix,
          strength: Number(strength.toFixed(2)),
          contribution: Number((strength * ix.weight).toFixed(3)),
        };
      })
      .filter(Boolean)
      .sort((x, y) => y.contribution - x.contribution);

    /* Exposure. A hazard harms nobody where there is nobody: heat is a
     * theatre-wide reading, but an empty Gangapur at 44°C is not an
     * elevated-risk sector and must not be coloured as one. Risk is hazard
     * multiplied by the population exposed to it, reaching full weight once
     * a sector is a quarter occupied. Without this the panel reads
     * "elevated" everywhere every afternoon, which is the same failure as
     * an alarm that is always on. */
    const exposure = clamp(sector.current / (0.25 * sector.capacity));

    const singleWorst = strongest?.severity ?? 0;
    const interactionTotal = active.reduce((a, ix) => a + ix.contribution, 0);
    const compound = Number(
      Math.max(0, Math.min(1, (singleWorst + interactionTotal) * exposure)).toFixed(3),
    );

    return {
      id: sector.id,
      name: sector.name,
      centre: sector.centre,
      hazards,
      unknown: unknown.map((h) => ({ key: h.key, reading: h.reading })),
      unmonitored: unmonitoredHere.map((h) => ({
        key: h.key, label: h.label, reason: h.reading,
      })),
      notApplicable: notApplicable.map((h) => ({ key: h.key, reading: h.reading })),
      /* Coverage, per sector. §16 reads this: a risk picture assembled from
       * nine of sixteen hazards is a different claim from a complete one,
       * and the commander is entitled to know which they are looking at. */
      coverage: {
        total: hazards.length,
        monitored: hazards.filter((h) => h.monitored !== false).length,
        computed: hazards.filter((h) => h.severity != null).length,
        blind: unmonitoredHere.length,
      },
      strongest: strongest
        ? {
            key: strongest.key,
            label: strongest.label,
            severity: Number(strongest.severity.toFixed(2)),
            reading: strongest.reading,
          }
        : null,
      exposure: Number(exposure.toFixed(2)),
      /* Compared against the worst single hazard with the SAME exposure
       * applied, so amplification isolates the correlation effect and is
       * never confused with the exposure discount. */
      singleWorst: Number(Math.min(1, singleWorst * exposure).toFixed(3)),
      interactions: active,
      compound,
      /* The number that justifies the section existing: how much worse the
       * correlated picture is than the worst single gauge. */
      amplification: Number(
        Math.max(0, compound - Math.min(1, singleWorst * exposure)).toFixed(3),
      ),
      level:
        compound >= 0.75 ? "severe"
        : compound >= 0.5 ? "elevated"
        : compound >= 0.3 ? "watch" : "nominal",
      narrative: narrate(sector, strongest, active, compound, singleWorst),
      trust: TRUST.SIMULATED,
    };
  });

  sectors.sort((a, b) => b.compound - a.compound);

  /* Theatre-level coverage. Identical across sectors by construction, but
   * computed rather than asserted so it cannot drift from the hazard list. */
  const sample = sectors[0]?.hazards ?? [];
  const blind = sample.filter((h) => h.monitored === false);

  return {
    environment: env,
    sectors,
    /* Theatre-level: the sectors where correlation changed the answer. */
    amplified: sectors.filter((s) => s.amplification >= 0.1),
    coverage: {
      hazardTypes: sample.length,
      monitored: sample.length - blind.length,
      blind: blind.length,
      blindList: blind.map((h) => ({ key: h.key, label: h.label, reason: h.reading })),
      /* The sentence a command centre should read before trusting the
       * risk picture above it. */
      statement: blind.length === 0
        ? `All ${sample.length} hazard types are monitored.`
        : `${sample.length - blind.length} of ${sample.length} hazard types are monitored. ${blind.length} — ${blind.map((h) => h.label.en.toLowerCase()).join(", ")} — have no data source in this platform and are not included in any risk score.`,
    },
  };
}

function narrate(sector, strongest, interactions, compound, singleWorst) {
  const name = sector.name?.en ?? sector.id;
  if (!strongest) return `${name}: no hazard above reporting threshold.`;
  if (!interactions.length) {
    return `${name}: ${strongest.label.en.toLowerCase()} is the only hazard in play (${strongest.reading}). No compounding pair is active.`;
  }
  const top = interactions[0];
  const pct = Math.round(((compound - singleWorst) / Math.max(0.01, singleWorst)) * 100);
  return `${name}: ${top.pair[0]} and ${top.pair[1]} are elevated together, which raises the risk ${pct}% above what the worst single reading (${strongest.label.en.toLowerCase()}, ${strongest.reading}) would suggest on its own.`;
}

export const HAZARD_ASSUMPTIONS = [
  "This is a stated rule set, not a learned model. Interaction weights are engineering judgement published for challenge — there is no incident history for a Kumbh that has not happened, so nothing here is fitted to data.",
  "Weather is a deterministic diurnal curve for a hot pre-monsoon day at Nashik, not a forecast. It exists so heat moves with the same clock as the crowd.",
  "Medical presentation rate is derived from heat and density rather than measured. Real deployment reads it from the health department's live case feed.",
  "A hazard with no measurement contributes nothing and is listed as unknown. It is never treated as zero — an unmapped sector must not rank as safe.",
  "Compound score is the strongest single hazard plus live interaction terms, capped at 1.0. A sector can therefore score above every individual gauge, which is the entire point of correlating them.",
];
